import { useUTLSettingsContext } from "@dexteel/mesf-core";
import { Grid, Typography } from "@mui/material";
import { get } from "lodash-es";
import { Moment } from "moment";
import { Controller, useForm } from "react-hook-form";
import { OperatorSelector } from "../../../../shared/selectors/OperatorSelector";
import { EndFilter, StartFilter } from "../../../delays/shared/StartEndFilter";
import { HeatFilter, HeatFilterOption } from "./HeatFilter";
import { LocationFilter } from "./LocationFilter";
import {
  TransactionTypeFilter,
  TransactionTypeOption,
} from "./TransactionTypeFilter";

type FilterFormInputs = {
  startDate: Moment;
  endDate: Moment;
  operatorId: number | null;
  heatId: number | null;
  operationTypeCode: string | null;
  locationId: number | null;
};

type Props = {
  onFilterChange: (startDate: Moment, endDate: Moment) => void;
  initialStartDate: Moment;
  initialEndDate: Moment;
  initialOperator: number | null;
  onChangeOperator: (id: number | null, name?: string) => void;
  heatOptions: HeatFilterOption[];
  initialHeat: number | null;
  onChangeHeat: (id: number | null) => void;
  typeOptions: TransactionTypeOption[];
  initialOperationType: string | null;
  onChangeOperationType: (code: string | null) => void;
  initialLocation: number | null;
  onChangeLocation: (id: number | null, name?: string) => void;
};

export const Filters = ({
  onFilterChange,
  initialStartDate,
  initialEndDate,
  initialOperator,
  onChangeOperator,
  heatOptions,
  initialHeat,
  onChangeHeat,
  typeOptions,
  initialOperationType,
  onChangeOperationType,
  initialLocation,
  onChangeLocation,
}: Props) => {
  const { state } = useUTLSettingsContext();
  const BYIId = parseInt(get(state, "settings.BYI", 14));

  const {
    control,
    formState: { errors },
    setValue,
    getValues,
  } = useForm<FilterFormInputs>({
    defaultValues: {
      startDate: initialStartDate,
      endDate: initialEndDate,
      operatorId: initialOperator,
      heatId: initialHeat,
      operationTypeCode: initialOperationType,
      locationId: initialLocation,
    },
    mode: "onChange",
  });

  const handleDateChange = async (
    date: Moment | null,
    fieldName: "startDate" | "endDate",
  ) => {
    if (date) {
      setValue(fieldName, date, { shouldValidate: true });

      const { startDate, endDate } = getValues();

      if (startDate && endDate && startDate.isBefore(endDate)) {
        onFilterChange(startDate, endDate);
      }
    }
  };

  const handleOperatorChange = (id: number | null, name?: string) => {
    setValue("operatorId", id);

    const { startDate, endDate } = getValues();

    if (startDate && endDate && startDate.isBefore(endDate)) {
      onChangeOperator(id, name);
    }
  };

  const handleHeatChange = (id: number | null) => {
    setValue("heatId", id);
    onChangeHeat(id);
  };

  const handleOperationTypeChange = (code: string | null) => {
    setValue("operationTypeCode", code);
    onChangeOperationType(code);
  };

  const handleLocationChange = (id: number | null, name?: string) => {
    setValue("locationId", id);
    onChangeLocation(id, name);
  };

  return (
    <>
      <Grid item md={2} xs={12}>
        <Controller
          name="startDate"
          control={control}
          rules={{
            required: "Start date is required",
            validate: (value, formValue) => {
              if (!value) return "Start date is required";
              if (formValue && !value.isBefore(formValue.endDate)) {
                return "Start date must be before end date";
              }
              return true;
            },
          }}
          render={({ field }) => (
            <StartFilter
              {...field}
              onChange={(date) => handleDateChange(date, "startDate")}
            />
          )}
        />
        {errors.startDate && (
          <Typography
            sx={{
              color: "error.main",
              fontSize: "0.75rem",
              mt: 0.5,
              ml: 1,
            }}
          >
            {errors.startDate.message}
          </Typography>
        )}
      </Grid>
      <Grid item md={2} xs={12}>
        <Controller
          name="endDate"
          control={control}
          rules={{
            required: "End date is required",
            validate: (value, formValue) => {
              if (!value) return "End date is required";
              if (formValue && !value.isAfter(formValue.startDate)) {
                return "End date must be after start date";
              }
              return true;
            },
          }}
          render={({ field }) => (
            <EndFilter
              {...field}
              onChange={(date) => handleDateChange(date, "endDate")}
            />
          )}
        />
        {errors.endDate && (
          <Typography
            sx={{
              color: "error.main",
              fontSize: "0.75rem",
              mt: 0.5,
              ml: 1,
            }}
          >
            {errors.endDate.message}
          </Typography>
        )}
      </Grid>
      <Grid item md={2} xs={12}>
        <Controller
          name="heatId"
          control={control}
          render={({ field }) => (
            <HeatFilter
              options={heatOptions}
              value={field.value}
              onChange={handleHeatChange}
            />
          )}
        />
      </Grid>
      <Grid item md={2} xs={12}>
        <Controller
          name="operationTypeCode"
          control={control}
          render={({ field }) => (
            <TransactionTypeFilter
              options={typeOptions}
              value={field.value}
              onChange={handleOperationTypeChange}
            />
          )}
        />
      </Grid>
      <Grid item md={2} xs={12}>
        <Controller
          name="locationId"
          control={control}
          render={({ field }) => (
            <LocationFilter
              value={field.value}
              onChange={handleLocationChange}
            />
          )}
        />
      </Grid>
      <Grid item md={2} xs={12}>
        <Controller
          name="operatorId"
          control={control}
          render={({ field }) => (
            <OperatorSelector
              assetId={BYIId}
              label="Operators"
              value={field.value}
              onChange={handleOperatorChange}
              showAllOption={true}
              disableClearable={true}
            />
          )}
        />
      </Grid>
    </>
  );
};
